import { DatePipe } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { selectInfo, selectNraIndicator } from '../../../account-info-header/reducers';
import { SelectControl } from '../../../create-request/models/OptionDropdownSelect';
import { AchInfo } from '../../../create-request/models/ach/AchInfo';
import { TaxWithholdingFields } from '../../../create-request/models/form/TransactionForm';
import { currencyStringToNumber } from '../../../create-request/utilities/currencyStringToNumber';
import {
  buildFromPrefilledForm,
  resetGroup,
} from '../../../create-request/utilities/formUtilities';
import { FormatDatePipe } from '../../../shared/pipes/FormatDatePipe';
import { AmountType } from '../../../shared/types/Transaction';
import {
  ApiEndpointType,
  Constants,
  transactionDirection,
} from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { mmddyyyyWithSlashes } from '../../../shared/utilities/regex-patterns';
import {
  StandingAuthDetailsActions,
  StandingAuthFormActions,
} from '../../actions';
import {
  StandingAuthDetails,
  StandingAuthFormServiceError,
  StandingAuthorizationForm,
  isAchStandingAuthDetails,
} from '../../models/standingAuthorizationForm';
import {
  selectACHAvailableCashMMF,
  selectACHAvailableCashMargin,
  selectAchAmountType,
  selectAchDefaultProcessDate,
  selectAchDistributionReason,
  selectAchInquiryCallFinished,
  selectAchVerifyCallError,
  selectCompletionDate,
  selectContributionOptions,
  selectFrequency,
  selectFundsAvailableToTrade,
  selectIsFromEditInProgress,
  selectMaxTransferAmount,
  selectOpenTwePopupConditions,
  selectSettlementDate,
  selectStandingAuthAchInfo,
  selectStandingAuthForm,
  selectStandingAuthFormDirection,
  selectStandingAuthFormServiceError,
  selectStandingAuthFormStandingAuthDetails,
  selectStandingAuthIdSA,
  selectTaxWithholding,
  selectWithdrawalDate
} from '../../reducers';
import { DistributionReason } from '../../../create-request/models/internal-transfer/DistributionReason';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-details-ach',
  templateUrl: './sa-details-ach.component.html',
  styleUrls: ['./sa-details-ach.component.scss'],
})
export class StandingAuthorizationAchComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPrompts: StandingAuthDetails | undefined;
  standingAuthorizationDetailsGroup: UntypedFormGroup;
  datePickerFormControlName: string = 'completionDate';
  completionDateLabelName: string = Constants.completionDateLabel;
  endDateLabelName: string = Constants.endDateLabel;
  secondCompletionDateLabelName: string = Constants.secondCompletionDateLabel;
  expectedWithdrawalDateCopy: string = Constants.expectedWithdrawalDateCopy;
  withdrawalDateServiceError: string = Constants.withdrawalDateErrorMessage;
  fundsAvailableToTradeCopy: string = Constants.fundsAvailableToTrade;
  standingAuthDetails$: Observable<StandingAuthDetails | undefined>;
  maxTransferAmount$: Observable<string>;
  settlementDate$: Observable<string>;
  taxWithholding$: Observable<string>;
  frequency: string | undefined;
  amountType$: Observable<AmountType>;
  fundsAvailableToTrade$: Observable<string>;
  contributionOptions$: Observable<string[]>;
  destroy$: Subject<boolean> = new Subject();
  isIra: boolean;
  transferDirection: string;
  withdrawalDate: string;
  maxDate: Date = new Date(new Date(3000, 11, 31).setHours(0, 0, 0, 0));
  readonly controlNames = {
    frequency: 'recurringFrequency',
    withdrawalDate: 'withdrawalDate',
    completionDate: 'completionDate',
    settlementDate: 'settlementDate',
    amount: 'amount',
    contributionReason: 'contributionReason',
    fundsAvailableToTrade: 'fundsAvailableToTrade'
  };
  inquiryCallFinished: boolean;
  taxWithHolding: TaxWithholdingFields;
  standingAuthFormState: StandingAuthorizationForm;
  hasWithdrawalDateServiceError: boolean;
  standingAuthFormServiceError$: Observable<StandingAuthFormServiceError>;
  hasServiceError: boolean = false;
  moneylinkVerifyError: boolean;
  availableCashMMF: string;
  availableCashMargin: string;
  achInfo: AchInfo;
  test: any;
  showSecondCompletionDate: boolean;
  datePickerValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
  ];
  endDateValidators: ValidatorFn[] = [
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate
  ];
  recurringFrequencyModel: SelectControl = {
    title: 'Set frequency of recurring transfer',
    controlName: 'recurringFrequency',
    options: [],
    validationMessage: `Please select 'Frequency' from dropdown.`,
  };
  formatDatePipe = new FormatDatePipe();
  withdrawalDate$: Observable<string>;
  defaultProcessDate: string;
  isNraAccount: boolean;
  hasATSSystemError: boolean;
  atsSystemErrorText: string;
  distributionReason$: Observable<DistributionReason>;
  achFundsSettlementBannerText: string = Constants.achFundsSettlementBannerText;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private datepipe: DatePipe,
  ) {}

  ngOnInit(): void {
    this.standingAuthorizationDetailsGroup = this.fb.group({});

    this.parentForm.setControl('standingAuthorizationDetails', this.standingAuthorizationDetailsGroup);

    this.store.select(selectInfo).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.isIra = val.classifications.isIra;
    });

    this.store.select(selectStandingAuthFormDirection).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.transferDirection = val;
    });

    this.store.select(selectFrequency).pipe(takeUntil(this.destroy$)).subscribe((info) => {
      this.frequency = info;
      if(info !== "On-Request"){
        this.standingAuthorizationDetailsGroup.setControl(this.controlNames.withdrawalDate, new FormControl('', Validators.required))
      }
    });

    this.store.select(selectStandingAuthFormDirection).pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if (val === transactionDirection.in.toLowerCase() && this.frequency !== "On-Request") {
        this.standingAuthorizationDetailsGroup.setControl(this.controlNames.settlementDate, new FormControl('', Validators.required));
        this.standingAuthorizationDetailsGroup.setControl(this.controlNames.fundsAvailableToTrade, new FormControl('', Validators.required));
      }
    });

    this.standingAuthDetails$ = this.store.pipe(
      select(selectStandingAuthFormStandingAuthDetails),
      map((details) =>isAchStandingAuthDetails(details) ? details : undefined)
    );

    this.store.select(selectACHAvailableCashMMF).pipe(takeUntil(this.destroy$)).subscribe((info) => {
      this.availableCashMMF = info;
    });

    this.store.select(selectACHAvailableCashMargin).pipe(takeUntil(this.destroy$)).subscribe((info) => {
      this.availableCashMargin = info;
    });

    this.store.select(selectNraIndicator).pipe(takeUntil(this.destroy$)).subscribe((nraIndicator) => {
      this.isNraAccount = nraIndicator ?? false;
    });

    this.distributionReason$ = this.store.select(selectAchDistributionReason).pipe(takeUntil(this.destroy$), map((distributionReason) => {
      let reason: DistributionReason = {
        code: '',
        subCode: '',
        description: distributionReason,
        isTaxExempt: false
      }
      return reason;
    }));

    this.withdrawalDate$ = this.store.pipe(select(selectWithdrawalDate));
    this.withdrawalDate$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      // emitEvent set to false to avoid resetting withdrawalDate infinitely
      this.standingAuthorizationDetailsGroup.controls.withdrawalDate?.setValue(val, { emitEvent: false }) });

    this.settlementDate$ = this.store.pipe(select(selectSettlementDate));
    this.settlementDate$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.standingAuthorizationDetailsGroup.controls.settlementDate?.setValue(val, { emitEvent: false })
    });

    this.fundsAvailableToTrade$ = this.store.pipe(select(selectFundsAvailableToTrade));
    this.fundsAvailableToTrade$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      this.standingAuthorizationDetailsGroup.controls.fundsAvailableToTrade?.setValue(val, { emitEvent: false })
    });

    this.store.select(selectTaxWithholding).pipe(takeUntil(this.destroy$)).subscribe((val) => (this.taxWithHolding = val));
    this.amountType$ = this.store.select(selectAchAmountType);

    this.store.select(selectStandingAuthForm).pipe(takeUntil(this.destroy$)).subscribe((val) => (this.standingAuthFormState = val));
    this.store.select(selectAchInquiryCallFinished).pipe(takeUntil(this.destroy$)).subscribe((callFinished) => {this.inquiryCallFinished = callFinished;});

    this.store.select(selectOpenTwePopupConditions).pipe(takeUntil(this.destroy$)).subscribe((tweConditions) => {
        const { verifyCallFinished, openTwePopup } = tweConditions;
        if (verifyCallFinished && !openTwePopup)
          this.store.dispatch(StandingAuthFormActions.loadReviewPage());
    });

    this.contributionOptions$ = this.store.select(selectContributionOptions);

    this.maxTransferAmount$ = this.store.select(selectMaxTransferAmount);

    this.store.select(selectCompletionDate).pipe(takeUntil(this.destroy$)).subscribe((date) => {
      if (date && this.standingAuthorizationDetailsGroup.controls.completionDate?.status === 'VALID') {
        const completionDate = this.datepipe.transform(date, 'yyyy-MM-dd') ?? '';
        this.store.dispatch(StandingAuthDetailsActions.loadWithdrawalDate({ completionDate: completionDate }));
      }
      this.standingAuthorizationDetailsGroup.controls.withdrawalDate?.setValue('', { emitEvent: false });
    });

    this.store
      .select(selectStandingAuthFormServiceError)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (val) =>
          (this.hasWithdrawalDateServiceError =
            val.name === ApiEndpointType.withdrawalDate)
      );

    this.standingAuthFormServiceError$ = this.store.select(selectStandingAuthFormServiceError);
    this.standingAuthFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      if ((val.url.includes('MoneyLinkVerify') && val.status.toString().startsWith('4')) || val.status.toString().startsWith('5')) {
        this.moneylinkVerifyError = this.hasServiceError = true;
      } else {
        this.moneylinkVerifyError = this.hasServiceError = false;
      }
    });

    // RESET TRANSACTION DETAILS WHEN STANDING AUTH ID IS SWITCHED
    this.store.pipe(
      takeUntil(this.destroy$),
      select(selectStandingAuthIdSA),
      distinctUntilChanged()
    ).subscribe((_) => this.resetDetails());

    this.store
      .select(selectIsFromEditInProgress)
      .pipe(
        takeUntil(this.destroy$),
      )
      .subscribe((_isFromEdit) => {
        buildFromPrefilledForm(
          this.prefilledPrompts,
          this.standingAuthorizationDetailsGroup,
          undefined
        );
        this.store.dispatch(StandingAuthFormActions.editLoadComplete());
      });

    this.store.select(selectStandingAuthAchInfo).pipe(takeUntil(this.destroy$)).subscribe((achInfo) => {this.achInfo = achInfo;});

    if (!this.isRecurring) {
      this.endDateValidators.push(CustomValidators.notAWeekend);
    }

    this.store.select(selectAchDefaultProcessDate).pipe(takeUntil(this.destroy$)).subscribe(processDate => {
      this.defaultProcessDate = processDate;
    });

    this.standingAuthorizationDetailsGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(form => {
      if (form.completionDate) {
        this.standingAuthorizationDetailsGroup.controls.completionDate?.addValidators(CustomValidators.notLessThanMinDate(new Date(this.defaultProcessDate)));
      }
    })

    this.store.select(selectAchVerifyCallError).pipe(takeUntil(this.destroy$)).subscribe(error => {
      this.hasATSSystemError = !!error;
      this.atsSystemErrorText = error;
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
    this.standingAuthorizationDetailsGroup.removeControl(this.controlNames.contributionReason);
  }

  get StandingAuthDetailsFormGroup() {
    return this.parentForm.controls?.standingAuthorizationDetails as UntypedFormGroup;
  }

  get taxWithholdingsFormGroupDetails() {
    if (isAchStandingAuthDetails(this.prefilledPrompts)) {
      return this.prefilledPrompts?.taxWithholdingsFormGroup;
    }
  }

  get showContributionReason(): boolean {
    return this.isIra && this.isIncoming;
  }

  get isIncoming(): boolean {
    return this.transferDirection === transactionDirection.in.toLowerCase();
  }

  get isRecurring() {
    // frequency is not set on the transaction flow so this.frequency is needed to not show the end date on the review page
    return this.frequency && this.frequency !== 'On-Request';
  }

  get availableCash(): number {
    return (currencyStringToNumber(this.availableCashMargin) + currencyStringToNumber(this.availableCashMMF));
  }

  // We do +2 here because the completion date is always at least +1 (tomorrow)
  get overdraftRulesFutureDate(): Date {
    let today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 2));
  }

  resetDetails(): void {
    resetGroup(this.standingAuthorizationDetailsGroup);
  }

  onFrequencyChanged(frequency: string): void {
    if (frequency === 'SEMIMTH') {
      this.showSecondCompletionDate = true;
    } else {
      this.showSecondCompletionDate = false;
    }
  }

  get displayTaxwithholdingsFields() {
    return this.isIra && !this.isIncoming && this.inquiryCallFinished;
  }

  get displayDetachedAmountType() {
    return this.displayTaxwithholdingsFields && !this.isRecurringAch && !this.isNraAccount;
  }

  get isRecurringAch() {
    return this.achInfo.frequency != 'On-Request';
  }

  get minDate(): Date {
    return new Date(this.defaultProcessDate);
  }
}
